import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Sidebar } from "@/components/Sidebar";
import DashboardPage from "@/pages/DashboardPage";
import PipelinePage from "@/pages/PipelinePage";
import LeadsPage from "@/pages/LeadsPage";
import ScraperPage from "@/pages/ScraperPage";
import ActivitiesPage from "@/pages/ActivitiesPage";
import CertificatesPage from "@/pages/CertificatesPage";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CommandPalette } from "@/components/CommandPalette";
import api, { setToken, removeToken } from "@/lib/api";

// ── Google Auth Callback (processes ?code= from Google OAuth redirect) ──
const GoogleAuthCallback = () => {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const errorParam = params.get("error");

    if (errorParam) {
      setError("Google sign-in was cancelled or denied. Please try again.");
      return;
    }

    if (!code) {
      setError("No authorisation code received. Please try signing in again.");
      return;
    }

    const redirectUri = window.location.origin + '/auth/callback';

    const exchange = async () => {
      try {
        const res = await api.post("/auth/google/exchange", { code, redirect_uri: redirectUri });
        setToken(res.data.session_token);
        setUser(res.data.user);
        window.location.replace("/dashboard");
      } catch (err) {
        console.error("[GoogleAuthCallback] Exchange failed:", err.response?.status, err.response?.data, err.message);
        removeToken();
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message || "Sign in failed. Please try again.";
        setError(status ? `${detail} (HTTP ${status})` : detail);
      }
    };

    exchange();
  }, [setUser]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="flex flex-col items-center gap-6 max-w-sm text-center p-8">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <div>
            <p className="text-base font-semibold text-foreground mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              SIGN IN FAILED
            </p>
            <p className="text-sm text-red-400" data-testid="auth-error-message">{error}</p>
          </div>
          <button
            onClick={() => { removeToken(); window.location.replace("/login"); }}
            className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white text-xs uppercase tracking-wider py-2.5 px-6"
            data-testid="auth-error-retry"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-muted-foreground">Signing you in...</p>
      </div>
    </div>
  );
};

// ── Protected Layout ──
const ProtectedLayout = () => {
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMd, setIsMd] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const handler = () => setIsMd(window.innerWidth >= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <div className="noise-overlay" />
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
        user={user}
        onLogout={logout}
      />
      <main
        className="flex-1 overflow-y-auto transition-[margin] duration-200 ease-in-out"
        style={{ marginLeft: isMd ? (sidebarCollapsed ? '72px' : '240px') : '0' }}
      >
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between h-14 px-4 border-b border-border bg-card sticky top-0 z-30">
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-sm text-foreground hover:bg-white/5"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <span className="text-sm font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            GASFLOW PRO
          </span>
          <div className="w-8" />
        </div>

        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px]">
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/pipeline" element={<PipelinePage />} />
            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/activities" element={<ActivitiesPage />} />
            <Route path="/certificates" element={<CertificatesPage />} />
            <Route path="/scraper" element={<ScraperPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </main>

      {/* Global command palette — available on all authenticated pages */}
      <CommandPalette />
    </div>
  );
};

// ── App Router ──
const AppRouter = () => {
  const location = useLocation();

  // Handle Google OAuth callback before running auth check
  if (location.pathname === '/auth/callback') {
    return (
      <AuthProvider>
        <GoogleAuthCallback />
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
};

// Separated so useAuth() is called inside AuthProvider tree
const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      <Route path="/*" element={user ? <ProtectedLayout /> : <Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Toaster theme="dark" position="bottom-right" />
    </BrowserRouter>
  );
}

export default App;
